import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { theme } from '../../../theme';
import moment from 'moment';

const MarkAttendance = ({ appointment, onUpdate }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Vérifier si le RDV est passé ou en cours
  const now = moment();
  const startTime = moment(appointment.start_time);
  const isPastOrCurrent = startTime.isSameOrBefore(now);
  const isNotMarked = appointment.client_showed_up === null || appointment.client_showed_up === undefined;

  // Ne rien afficher si le RDV est futur
  if (!isPastOrCurrent) {
    return null;
  }

  const handleMarkAttendance = async (showedUp) => {
    // Vérification supplémentaire avant de marquer
    if (appointment.client_showed_up !== null && appointment.client_showed_up !== undefined) {
      const status = appointment.client_showed_up ? 'présent' : 'absent';
      setSnackbar({ 
        open: true, 
        message: `La présence a déjà été marquée pour ce rendez-vous (client ${status}).`, 
        severity: 'info' 
      });
      setOpenDialog(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/appointments/${appointment.appointment_id}/mark_attendance/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ showed_up: showedUp })
      });

      const data = await response.json();

      if (response.ok) {
        let message = showedUp ? 'Client marqué comme présent.' : 'Client marqué comme absent.';
        
        // Ajouter les infos de sanction si absence
        if (!showedUp && data.sanction) {
          const { total_no_shows, sanction_applied } = data.sanction;
          
          if (sanction_applied === 'warning') {
            message += ` ⚠️ Avertissement envoyé (${total_no_shows} absence${total_no_shows > 1 ? 's' : ''}).`;
          } else if (sanction_applied === 'temporary_ban') {
            message += ` 🚫 Client suspendu 1 semaine (${total_no_shows} absences).`;
          } else if (sanction_applied === 'permanent_ban') {
            message += ` 🚫 Client banni définitivement (${total_no_shows} absences).`;
          }
        }

        setSnackbar({ 
          open: true, 
          message, 
          severity: showedUp ? 'success' : 'warning' 
        });
        setOpenDialog(false);
        
        // Rafraîchir la liste
        if (onUpdate) onUpdate();
      } else {
        setSnackbar({ 
          open: true, 
          message: data.detail || 'Erreur lors du marquage', 
          severity: 'error' 
        });
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      setSnackbar({ 
        open: true, 
        message: 'Erreur réseau', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Afficher l'icône appropriée */}
      {isNotMarked ? (
        // Boutons pour marquer
        <>
          <Tooltip title={loading ? "Traitement en cours..." : "Marquer comme absent"}>
            <span>
              <IconButton
                size="small"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDialog(true);
                }}
                sx={{
                  color: theme.colors.warning[500],
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: theme.colors.warning[500],
                    color: '#fff',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  }
                }}
              >
                <PersonOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title={loading ? "Traitement en cours..." : "Marquer comme présent"}>
            <span>
              <IconButton
                size="small"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAttendance(true);
                }}
                sx={{
                  color: theme.colors.success[500],
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: theme.colors.success[500],
                    color: '#fff',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  }
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : appointment.client_showed_up ? (
        // Déjà marqué présent
        <Tooltip title="Client présent">
          <CheckCircleIcon 
            fontSize="small" 
            sx={{ color: theme.colors.success[500] }} 
          />
        </Tooltip>
      ) : (
        // Déjà marqué absent
        <Tooltip title="Client absent">
          <CancelIcon 
            fontSize="small" 
            sx={{ color: theme.colors.error[500] }} 
          />
        </Tooltip>
      )}

      {/* Dialog de confirmation pour absence */}
      <Dialog 
        open={openDialog} 
        onClose={() => !loading && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirmer l'absence
        </DialogTitle>
        <DialogContent>
          <p>
            Êtes-vous sûr que <strong>{appointment.client_name}</strong> ne s'est pas présenté à ce rendez-vous ?
          </p>
          <p style={{ color: theme.colors.warning[600], marginTop: '10px', fontSize: '0.9rem' }}>
            ⚠️ Cette action déclenchera des sanctions automatiques en cas d'absences répétées :
          </p>
          <ul style={{ color: theme.colors.text.secondary, fontSize: '0.85rem', marginTop: '8px' }}>
            <li>1ère absence : Avertissement</li>
            <li>2ème absence : Suspension 1 semaine</li>
            <li>3ème absence : Ban définitif</li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button 
            onClick={() => handleMarkAttendance(false)} 
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: theme.colors.warning[500],
              '&:hover': {
                backgroundColor: theme.colors.warning[600],
              }
            }}
          >
            {loading ? 'Traitement...' : 'Confirmer l\'absence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast de notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MarkAttendance;
